/**
 * Platform Terms & Conditions acceptance — account-level, same idea as
 * POPIA consent: version + timestamp + method on the User document.
 *
 * Bump TERMS_CONSENT_VERSION when the published legal pack changes so
 * existing accounts must accept again.
 */
export const TERMS_CONSENT_VERSION = '2026-09-09';
export const TERMS_CONSENT_SCOPE =
    'I have read and agree to the IPM Terms of Service and Privacy Policy.';

const EXEMPT_EMAILS = new Set();

const LOGIN_HOME_KEY = 'ipm.loginHome';

export function isTermsExemptEmail(email) {
    return EXEMPT_EMAILS.has(String(email || '').toLowerCase().trim());
}

export function acceptedTermsVersion(user) {
    if (!user) return '';
    const fromConsent = String(user.termsConsent?.version || '').trim();
    if (fromConsent) return fromConsent;
    return '';
}

function isIpmAdminUser(u) {
    return String(u?.role || '').toLowerCase() === 'admin';
}

function peekSessionJwt(token) {
    try {
        const part = String(token || '').split('.')[1];
        if (!part) return null;
        const padded = part.replace(/-/g, '+').replace(/_/g, '/');
        const json = atob(padded);
        return JSON.parse(json);
    } catch (_) {
        return null;
    }
}

function readLoginHome() {
    try {
        return JSON.parse(sessionStorage.getItem(LOGIN_HOME_KEY) || 'null');
    } catch (_) {
        return null;
    }
}

/** Remember the account that actually signed in (not a hop / demo seat). */
export function rememberLoginHome(data) {
    if (typeof window === 'undefined' || !data) return;
    try {
        const claims = peekSessionJwt(data.token);
        if (data._demoImpersonation || claims?.demo === true) return;
        const selfId = String(data._id || data.id || claims?.id || '');
        const homeId = String(claims?.home || selfId);
        if (!selfId || (homeId && homeId !== selfId)) return;
        if (isIpmAdminUser(data)) {
            sessionStorage.setItem(LOGIN_HOME_KEY, JSON.stringify({ id: selfId, role: 'admin' }));
        } else {
            sessionStorage.removeItem(LOGIN_HOME_KEY);
        }
    } catch (_) { /* ignore */ }
}

/** Admin signed in, or hopping onto another seat from that admin login. */
export function isExemptOperator() {
    if (typeof window === 'undefined') return false;
    try {
        const demoRaw = sessionStorage.getItem('ipm_demo_mode');
        const demo = demoRaw ? JSON.parse(demoRaw) : null;
        if (isIpmAdminUser(demo?.realUser)) return true;
        const prev = JSON.parse(localStorage.getItem('ipm_demo_prev_user') || 'null');
        if (isIpmAdminUser(prev)) return true;

        const user = JSON.parse(localStorage.getItem('user') || 'null');
        if (!user) return false;
        if (user._operatedByAdmin) return true;
        if (user._demoImpersonation && (isIpmAdminUser(demo?.realUser) || isIpmAdminUser(prev))) return true;

        const claims = peekSessionJwt(user.token);
        if (claims?.demo === true && (isIpmAdminUser(demo?.realUser) || isIpmAdminUser(prev))) return true;

        const opId = String(user._id || user.id || claims?.id || '');
        const homeId = claims?.home ? String(claims.home) : '';
        if (homeId && opId && homeId !== opId && String(claims.homeRole || '').toLowerCase() === 'admin') {
            return true;
        }
        const loginHome = readLoginHome();
        if (
            isIpmAdminUser(loginHome)
            && loginHome.id
            && opId
            && String(loginHome.id) !== opId
        ) {
            return true;
        }
    } catch (_) { /* ignore */ }
    return false;
}

export function hasAcceptedCurrentTerms(user) {
    if (!user) return false;
    if (isTermsExemptEmail(user.email) || isExemptOperator()) return true;
    if (user.termsConsent?.accepted === true && acceptedTermsVersion(user) === TERMS_CONSENT_VERSION) {
        return true;
    }
    return false;
}

export function needsTermsAcceptance(user) {
    if (!user) return false;
    if (isTermsExemptEmail(user.email) || isExemptOperator()) return false;
    return !hasAcceptedCurrentTerms(user);
}

/**
 * Hub invite /set-password wall. First-time seats that have never logged in
 * and have not accepted terms still go through it. Admins (and admin hops)
 * never do. Working logins and consented accounts skip it.
 */
export function sessionRequiresPasswordChange(user) {
    if (!user) return false;
    if (String(user.role || '').toLowerCase() === 'admin') return false;
    if (isTermsExemptEmail(user.email) || isExemptOperator()) return false;
    if (!user.mustChangePassword) return false;
    if (user.lastLoginAt) return false;
    if (hasAcceptedCurrentTerms(user)) return false;
    return true;
}
